import React, { useState, useEffect } from 'react';
import { FaEnvelope } from 'react-icons/fa';
import Modal from './Modal';
import ParticipantList from './ParticipantList';
import axios from "../axiosConfig";
import { useFieldOptions, fieldOptionList } from "./Utils";

const QuickEmail = ({ initialParticipants = [], onEmailSent }) => {
  const [user, setUser] = useState(null);
  const fieldOptions = useFieldOptions();
  const [emailForm, setEmailForm] = useState({ subject: "", body: "", type: "" });
  const [emailParticipants, setEmailParticipants] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  // Load user from localStorage
  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser) {
        setUser(storedUser);
        setEmailForm((prev) => ({
          ...prev,
          type: storedUser.settings?.default_interaction_type || ""
        }));
      }
    } catch {
      console.warn("Invalid user in localStorage");
    }
  }, []);

  // Update participants when initialParticipants change
  useEffect(() => {
    setEmailParticipants(initialParticipants);
  }, [initialParticipants]);

  // Email form handlers
  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({ ...prev, [name]: value }));
  };

  const generateMailtoLink = async (logInteraction = true) => {
    if (!emailParticipants.length || !user) return "#";

    const subject = encodeURIComponent(emailForm.subject || "");
    const body = encodeURIComponent(emailForm.body || "");

    // Collect emails from participants
    const emails = emailParticipants
      .map(p => p.email)
      .filter(e => !!e)
      .join(",");

    if (logInteraction) {
      // Ensure user is always included in participants
      const participants = [
        {
          id: user.user_contact_id,
          type: "contact",
          is_sender: true,
        },
        ...emailParticipants.filter(p => p.id !== user.user_contact_id || p.type !== "contact")
      ].map(p => ({ id: p.id, type: p.type, is_sender: p.is_sender }));

      const payload = {
        completed_at: new Date().toISOString(),
        content: `${emailForm.subject}\n\n${emailForm.body}`,
        format: "Email",
        type: emailForm.type,
        participants,
      };

      axios.post("/interactions/", payload, { withCredentials: true })
        .catch(err => console.error("Failed to log email interaction:", err));
    }

    return `mailto:${emails}?subject=${subject}&body=${body}`;
  };

  const handleSendEmail = async () => {
    const link = await generateMailtoLink(true);
    window.location.href = link;
    handleClose();
    // Call callback if provided
    if (onEmailSent) {
      onEmailSent();
    }
  };

  const handleSkipEmail = async () => {
    const link = await generateMailtoLink(false);
    window.location.href = link;
    handleClose();
  };

  const handleOpen = () => {
    setEmailParticipants(initialParticipants);
    setIsOpen(true);
  };

  const handleClose = () => {
    // Reset form when closing
    setEmailForm({ subject: "", body: "", type: user?.settings?.default_interaction_type || "" });
    setIsOpen(false);
  };

  // Get the first participant's email for display
  const firstParticipantEmail = initialParticipants.length > 0 ? initialParticipants[0].email : null;

  // If no email, render nothing
  if (!firstParticipantEmail) {
    return 'N/A';
  }

  return (
    <>
      <a href="#" onClick={(e) => { e.preventDefault(); handleOpen(); }}>
        <FaEnvelope /> {firstParticipantEmail}
      </a>
      
      <Modal isOpen={isOpen} onClose={handleClose} title="Email Participants">
        <div className="form">
          <ParticipantList
            participants={emailParticipants}
            onChange={setEmailParticipants}
            emailMode={true}
          />
          <label>
            Type:
            <select autoComplete="off"
              name="type"
              value={emailForm.type}
              onChange={handleEmailChange}
              required
            >
              <option value="">Please select...</option>
              {fieldOptionList(fieldOptions, "interactions", "type").map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label>Subject: <input autoComplete="off" type="text" name="subject" value={emailForm.subject} onChange={handleEmailChange} /></label>
          <label>Message: <textarea autoComplete="off" name="body" rows={5} value={emailForm.body} onChange={handleEmailChange} /></label>
          <div>
            <button
              onClick={handleSendEmail}
              disabled={
                !emailForm.subject.trim() ||
                !emailForm.type.trim() ||
                !emailForm.body.trim()
              }
            >
              Open Email App
            </button>
            <button onClick={handleSkipEmail}>
              Skip (just open email app)
            </button>
            <button onClick={handleClose}>Cancel</button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default QuickEmail;
